import { Flex, Stack, Text } from '@mantine/core';

import { Subscription, SubscriptionBillingCycle, SubscriptionType } from 'models';
import { IntlHelpers } from 'helpers/IntlHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';

import { usePrices } from 'hooks/use-prices';

export const PriceTag = ({
    subscription,
    billingCycle,
    alwaysShow,
}: {
    subscription: Subscription;
    billingCycle: SubscriptionBillingCycle;
    alwaysShow?: boolean;
}) => {
    const response = usePrices();

    if (!response.data) {
        return null;
    }

    const price = response.data[subscription][billingCycle];

    if (price === 0 && !alwaysShow) {
        return null;
    }

    const timeUnit = getBillingCycleTimeUnit(billingCycle);

    const lines = [timeUnit];

    if (SubscriptionHelpers.isSubscriptionType(SubscriptionType.CREDIT, subscription)) {
        lines.unshift('/ credit package');
    } else if (SubscriptionHelpers.isSubscriptionType(SubscriptionType.DESIGNER, subscription)) {
        lines.unshift('/ team');
    }

    return (
        <Flex
            gap={2}
            align="center"
            style={{ flexShrink: 0, display: 'inline-flex' }}
            component="span"
            data-price-wrapper
        >
            <span data-price>{<span style={{ lineHeight: 1 }}>{IntlHelpers.formatPrice(price)}</span>}</span>

            {lines.length > 0 && (
                <Stack gap={0} fz={10} c="dimmed" align="flex-start" component="span" data-lines>
                    {lines.map((line) => (
                        <span key={line} style={{ lineHeight: 0.9 }} data-line>
                            {line}
                        </span>
                    ))}
                </Stack>
            )}
        </Flex>
    );
};

const getBillingCycleTimeUnit = (billingCycle: SubscriptionBillingCycle) => {
    switch (billingCycle) {
        case SubscriptionBillingCycle.MONTHLY:
            return '/ month';
        case SubscriptionBillingCycle.YEARLY:
            return '/ year';
    }
};

export const PriceTagStacked = ({
    multiplier = 1,
    subscription,
    billingCycle,
    size = 'md',
    hidePeriod,
    alwaysShow,
}: {
    multiplier?: number;
    subscription: Subscription;
    billingCycle: SubscriptionBillingCycle;
    size?: 'sm' | 'md' | 'lg';
    hidePeriod?: boolean;
    alwaysShow?: boolean;
}) => {
    const response = usePrices();

    if (!response.data) {
        return null;
    }

    const price = response.data[subscription][billingCycle] * multiplier;

    if (price === 0 && !alwaysShow) {
        return null;
    }

    const periodLabel = hidePeriod ? '' : billingCycle === SubscriptionBillingCycle.YEARLY ? '/ year' : '/ mo';

    const sizes = {
        sm: { price: 14, period: 11 },
        md: { price: 16, period: 13 },
        lg: { price: 20, period: 14 },
    };

    const fontSize = sizes[size];

    const formattedPrice = IntlHelpers.formatPrice(price);

    return (
        <Stack gap={0} align="flex-end" style={{ flexShrink: 0 }}>
            <Text span fz={fontSize.price} fw={700} lh={1}>
                {formattedPrice}
            </Text>
            <Text span fz={fontSize.period} fw={400} c="dimmed" lh={1}>
                {periodLabel}
            </Text>
        </Stack>
    );
};
